import { matchRoutes, RouteObject } from 'react-router';

import { AppModule } from '../constants/AppModule';
import type { ModuleRouteConfigs } from '../types/ModuleRouteConfigs';

/**
 * Resolves which module owns `pathname` by matching it against the combined
 * react-router tree of every module's config, exactly as the tenant's real
 * router does. Each module's config is tagged with its AppModule as the route
 * id, so react-router's own ranking picks the single best match across all
 * modules (a module's catch-all "*" never shadows a specific route in another),
 * and the winning top-level match's id is the module. This naturally covers
 * nested children, tenant document routes, and "*" fallbacks without us
 * re-implementing path matching.
 *
 * Returns undefined when nothing matches (the provider treats that as an error).
 */
export const resolveModuleFromPath = (
  moduleRouteConfigs: ModuleRouteConfigs,
  pathname: string,
): AppModule | undefined => {
  const taggedRoutes: RouteObject[] = Object.entries(moduleRouteConfigs)
    .filter((entry): entry is [AppModule, RouteObject] => !!entry[1])
    .map(([module, config]) => ({ ...config, id: module }));

  const matches = matchRoutes(taggedRoutes, pathname);

  return (matches?.[0]?.route.id as AppModule | undefined) ?? undefined;
};
